import React from 'react';
import { Helmet } from 'react-helmet';
import { FaWhatsapp } from 'react-icons/fa';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import WhatsAppFloat from '../../components/WhatsAppFloat';
import Icon from '../../components/AppIcon';
import HeroSection from './components/HeroSection';
import LiveGamesPreview from './components/LiveGamesPreview';
import TrustIndicators from './components/TrustIndicators';
import VideoTestimonials from './components/VideoTestimonials';
import SocialProofMetrics from './components/SocialProofMetrics';
import FaqSection, { FAQS } from './components/FaqSection';
import { SITE, WA_MESSAGES, WHATSAPP_DISPLAY, openWhatsApp } from '../../config/site';

const PILLARS = [
  {
    icon: 'KeyRound',
    title: 'Nitin online book login',
    copy:
      'One-tap access to your account with 24/7 WhatsApp assistance if a device changes, an OTP is delayed or you simply forget where you left off.',
    accent: '#F0C24B',
  },
  {
    icon: 'UserPlus',
    title: 'Nitin online book sign up',
    copy:
      'Secure onboarding in about two minutes. KYC-friendly, no hidden charges, and a welcome bonus credited before your first hand.',
    accent: '#25D366',
  },
  {
    icon: 'Smartphone',
    title: 'Nitin online book app download',
    copy:
      'Signed Android and iOS builds delivered straight from the desk, with live game updates and real-time payout notifications.',
    accent: '#5B8CFF',
  },
];

const STEPS = [
  { n: '01', title: 'Message the desk', copy: `Send one WhatsApp to ${WHATSAPP_DISPLAY}. A real person answers, usually within two minutes.` },
  { n: '02', title: 'Get your ID & bonus', copy: 'Your Nitin Online Book ID is created on the spot, welcome bonus applied, limits set to your comfort.' },
  { n: '03', title: 'Play and withdraw', copy: 'Pick your table, play fair games, and cash out to UPI or your bank in about five minutes — any hour.' },
];

const HomepageRoyalWelcomeHub = () => {
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  return (
    <>
      <Helmet>
        {/* Deindexed: keep site reachable directly, but out of search results. */}
        <meta name="robots" content="noindex, nofollow, noarchive, nosnippet, noimageindex" />
        <meta name="googlebot" content="noindex, nofollow, noarchive, nosnippet, noimageindex" />
        <title>Nitin Online Book | Nitin Online Book - India's Best Gaming Platform</title>
        <meta
          name="description"
          content="Official Nitin Online Book and Nitin Online Book portal. Nitin online book login, sign up and app download with secure gaming India wide, instant 5-minute payouts and 24/7 WhatsApp support."
        />
        <meta
          name="keywords"
          content="official Nitin Online Book, official Nitin Online Book, Nitin online book login, Nitin online book sign up, Nitin online book app download, secure gaming India, best online betting platform, online gaming India, betting platform, WhatsApp support, instant payouts"
        />
        <meta property="og:title" content="Nitin Online Book | Nitin Online Book - India's Best Gaming Platform" />
        <meta
          property="og:description"
          content="Welcome to Nitin Online Book and Nitin Online Book, India's leading online gaming platform. Nitin online book login, sign up and app download with secure transactions, instant payouts and 24/7 WhatsApp support."
        />
        <meta property="og:image" content={`${SITE.url}/assets/images/gen/og-cover.svg`} />
        <meta property="og:url" content={`${SITE.url}/`} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={SITE.name} />
        <meta property="og:locale" content="en_IN" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Nitin Online Book | Nitin Online Book - India's Best Gaming Platform" />
        <meta
          name="twitter:description"
          content="Welcome to Nitin Online Book and Nitin Online Book, India's leading online gaming platform. Nitin online book login, sign up, app download. Secure transactions, instant payouts, 24/7 WhatsApp support."
        />
        <meta name="twitter:image" content={`${SITE.url}/assets/images/gen/og-cover.svg`} />
        <link rel="canonical" href={`${SITE.url}/`} />

        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: SITE.name,
            alternateName: SITE.altName,
            url: `${SITE.url}/`,
            description:
              "India's most trusted online gaming platform offering secure gaming, instant payouts and personalised 24/7 WhatsApp support.",
            potentialAction: {
              '@type': 'SearchAction',
              target: `${SITE.url}/search?q={search_term_string}`,
              'query-input': 'required name=search_term_string',
            },
            publisher: {
              '@type': 'Organization',
              name: SITE.name,
              url: `${SITE.url}/`,
              logo: `${SITE.url}/assets/images/logo.png`,
              contactPoint: {
                '@type': 'ContactPoint',
                telephone: '+91-7309764773',
                contactType: 'customer service',
                contactOption: 'WhatsApp',
                availableLanguage: ['English', 'Hindi'],
              },
            },
          })}
        </script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        <HeroSection />

        {/* ---------------- Official portal pillars ---------------- */}
        <section className="relative overflow-hidden py-24">
          <span className="aurora left-[-8%] top-[10%] h-96 w-96 bg-royal-gold/20" aria-hidden="true" />

          <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-3xl text-center">
              <span className="badge-pill"><Icon name="BadgeCheck" size={13} /> Verified &amp; official</span>
              <h2 className="mt-6 font-heading text-display font-bold text-white">
                The official <span className="gold-text">Nitin Online Book</span> &amp; Nitin Online Book portal
              </h2>
              <p className="mt-4 text-body-lg text-white/60">
                One address, one desk, one set of rules. This is the trusted entry point for login, sign-up and app
                download — transparent, secure and built so you can play smarter and win with confidence.
              </p>
            </div>

            <div className="perspective mt-14 grid gap-7 md:grid-cols-3">
              {PILLARS.map((p, i) => (
                <article key={p.title} className="card-3d animate-rise p-8" style={{ animationDelay: `${i * 90}ms` }}>
                  <span
                    className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl border border-white/10"
                    style={{ background: `linear-gradient(160deg, ${p.accent}33, ${p.accent}0D)` }}
                  >
                    <Icon name={p.icon} size={26} color={p.accent} />
                  </span>
                  <h3 className="font-heading text-xl font-bold text-white">{p.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-white/55">{p.copy}</p>
                  <button
                    onClick={() => openWhatsApp(WA_MESSAGES.general)}
                    className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-royal-gold transition hover:gap-3"
                  >
                    Start on WhatsApp <Icon name="ArrowRight" size={15} />
                  </button>
                </article>
              ))}
            </div>

            {/* Three-step flow */}
            <div className="mt-16 grid gap-6 md:grid-cols-3">
              {STEPS.map((s, i) => (
                <div key={s.n} className="relative">
                  <div className="card-flat h-full p-7">
                    <span className="font-heading text-4xl font-bold text-white/10">{s.n}</span>
                    <h3 className="mt-2 font-display text-lg font-semibold text-white">{s.title}</h3>
                    <p className="mt-2 text-sm text-white/55">{s.copy}</p>
                  </div>
                  {i < STEPS.length - 1 && (
                    <Icon
                      name="ChevronRight"
                      size={22}
                      className="absolute -right-4 top-1/2 hidden -translate-y-1/2 text-royal-gold/60 md:block"
                    />
                  )}
                </div>
              ))}
            </div>

            <div className="mt-12 text-center">
              <button onClick={() => openWhatsApp(WA_MESSAGES.newId)} className="btn-3d btn-3d-wa">
                <FaWhatsapp size={19} /> Claim your gaming ID now
              </button>
              <p className="mt-4 text-sm text-white/40">
                Proven fair play · quick withdrawals · friendly 24/7 support on {WHATSAPP_DISPLAY}
              </p>
            </div>
          </div>
        </section>

        <LiveGamesPreview />
        <TrustIndicators />
        <VideoTestimonials />
        <SocialProofMetrics />
        <FaqSection />
        <Footer />
        <WhatsAppFloat />
      </div>
    </>
  );
};

export default HomepageRoyalWelcomeHub;
